"use client";

/**
 * PostHogProvider — initializes PostHog once on mount, identifies the
 * logged-in Supabase user, and captures pageviews on route change.
 *
 * Design rules:
 * - If NEXT_PUBLIC_POSTHOG_KEY is not set, this component is a no-op so
 *   the app still runs during local dev / before keys are pasted in.
 * - Respects Do Not Track (DNT=1) browsers — skips init entirely.
 * - Only ever initializes once, even with React strict-mode double mount.
 * - Silently fails — never throws, never blocks render.
 */

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { createClient } from "@/lib/supabase/client";

let __initialized = false;

function initPostHog() {
  if (__initialized) return;
  if (typeof window === "undefined") return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";
  if (!key) return; // no key → silent no-op

  // Respect Do Not Track
  if (typeof navigator !== "undefined" && navigator.doNotTrack === "1") return;

  try {
    posthog.init(key, {
      api_host: host,
      capture_pageview: false,          // we handle pageviews manually below
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      autocapture: true,                // clicks, inputs, form submits
      disable_session_recording: true,  // opt-in later if needed
      loaded: (ph) => {
        if (process.env.NODE_ENV === "development") ph.debug(false);
      },
    });
    __initialized = true;
  } catch {
    // swallow — never break the app because analytics is sick
  }
}

export function PostHogProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Init once
  useEffect(() => {
    initPostHog();
  }, []);

  // Identify logged-in user
  useEffect(() => {
    if (!__initialized) return;
    const supabase = createClient();

    supabase.auth.getUser().then(({ data }) => {
      const u = data?.user;
      if (!u) return;
      try {
        posthog.identify(u.id, {
          email: u.email ?? undefined,
        });
      } catch {
        /* noop */
      }
    }).catch(() => {});

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!__initialized) return;
      try {
        if (event === "SIGNED_IN" && session?.user) {
          posthog.identify(session.user.id, {
            email: session.user.email ?? undefined,
          });
        } else if (event === "SIGNED_OUT") {
          posthog.reset();
        }
      } catch {
        /* noop */
      }
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  // Capture pageviews on route change
  useEffect(() => {
    if (!__initialized) return;
    if (!pathname) return;
    try {
      const url =
        pathname +
        (searchParams?.toString() ? `?${searchParams.toString()}` : "");
      posthog.capture("$pageview", { $current_url: url });
    } catch {
      /* noop */
    }
  }, [pathname, searchParams]);

  return null;
}

/**
 * Forwards a custom event to PostHog (if initialized). Safe to call
 * from anywhere — silently drops events when PostHog is not loaded.
 */
export function captureToPostHog(
  event_type: string,
  event_data: Record<string, unknown>,
) {
  if (!__initialized) return;
  try {
    posthog.capture(event_type, event_data);
  } catch {
    /* noop */
  }
}
